import { useProperties } from "@/hooks/useProperties";
import { supabase } from "@/integrations/supabase/client";
import { Enums, Tables } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";

export type Income = Tables<"property_income"> & { 
  guest_name?: string;
  property_id?: string;
  property_short_name?: string;
};

export interface IncomeFilters {
  propertyIds?: string[];
  bookingId?: string;
  incomeType?: Enums<"income_type">;
  paymentType?: Enums<"payment_type">;
  dateFrom?: string;
  dateTo?: string;
  searchTerm?: string;
  organizationIds?: string[];
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export const useIncome = (filters?: IncomeFilters, pagination?: PaginationParams) => {
  const { data: properties = [], isLoading: isLoadingProperties } =
    useProperties();

  const page = pagination?.page || 1;
  const pageSize = pagination?.pageSize || 100;

  return useQuery({
    queryKey: ["income", filters, pagination, properties.map((p) => p.id)],
    enabled: filters?.bookingId 
      ? true 
      : (!isLoadingProperties && properties.length > 0),
    queryFn: async (): Promise<PaginatedResult<Income>> => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Usar función RPC si hay searchTerm o organizationIds
      const useRpcQuery = filters?.searchTerm || (filters?.organizationIds && filters.organizationIds.length > 0);
      
      if (useRpcQuery) {
        const propertyIds =
          filters?.propertyIds?.length > 0
            ? filters.propertyIds
            : properties.map((p) => p.id);

        const { data, error } = await supabase.rpc('search_property_income', {
          p_search_term: filters.searchTerm || null,
          p_property_ids: propertyIds,
          p_payment_type: filters.paymentType || null,
          p_date_from: filters.dateFrom || null,
          p_date_to: filters.dateTo || null,
          p_page: page,
          p_page_size: pageSize,
          p_order_by: pagination?.orderBy || 'payout_date',
          p_order_direction: pagination?.orderDirection || 'desc',
          p_organization_ids: filters.organizationIds || null,
        });

        if (error) throw error;

        const totalCount = data && data.length > 0 ? Number(data[0].total_count) : 0;

        return {
          data: (data || []) as Income[],
          totalCount,
          totalPages: Math.ceil(totalCount / pageSize),
          currentPage: page,
        };
      }

      // Consulta estándar sin búsqueda
      let query = supabase
        .from("property_income")
        .select(`
          *,
          bookings!inner(
            booking_id,
            guest_name,
            property_short_name,
            property_id
          )
        `, { count: 'exact' })
        .range(from, to)
        .order(pagination?.orderBy || "payout_date", { 
          ascending: pagination?.orderDirection === 'asc' 
        });

      // Filter by bookingId or propertyIds
      if (filters?.bookingId) {
        query = query.eq("booking_id", filters.bookingId);
      } else {
        const propertyIds =
          filters?.propertyIds?.length > 0
            ? filters.propertyIds
            : properties.map((p) => p.id);
        
        query = query.in("bookings.property_id", propertyIds);
      }

      // Apply filters directly in the query
      if (filters?.incomeType) {
        query = query.eq("income_type", filters.incomeType);
      }

      if (filters?.paymentType) {
        query = query.eq("payment_type", filters.paymentType);
      }

      if (filters?.dateFrom) {
        query = query.gte("payout_date", filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte("payout_date", filters.dateTo);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      // Transform the data to match Income interface
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        guest_name: item.bookings?.guest_name || "",
        property_id: item.bookings?.property_id || "",
        property_short_name: item.bookings?.property_short_name || "",
      })) as Income[];

      return {
        data: transformedData,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
      };
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};
